// The admin page's sync monitor (SPEC §13.2): one row per worker module, its run history on click,
// the jobs queue and the watermarks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { formatWibLong, type RunPart, type SyncModuleState } from "@po/shared";
import { api } from "../lib/api";

type Run = {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  outcome: string;
  recordsChanged: number;
  full: boolean;
  errorType: string | null;
  errorMessage: string | null;
  parts: RunPart[] | null;
};
type Job = {
  id: number;
  type: string;
  no: string;
  state: string;
  createdAt: string;
  claimedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  progress: { done: number; total: number; step: string | null } | null;
};
type Module = {
  kind: string;
  label: string;
  source: string;
  schedule: string;
  overdueAfterMinutes: number | null;
  state: SyncModuleState;
  latest: Run | null;
  lastSuccess: Run | null;
  lastFailure: Run | null;
  failingSince: string | null;
  failuresInARow: number;
  last24h: { runs: number; failed: number; recordsChanged: number; avgDurationMs: number | null; maxDurationMs: number | null; strip: { at: string; outcome: string }[] };
};
type Worker = {
  name: string;
  host: string;
  pid: number;
  startedAt: string;
  beatAt: string;
  silentMs: number;
  alive: boolean;
  scheduled: boolean;
  busy: string[];
  jobsRunning: Record<string, number>;
};
type AutoSync = { on: boolean; otherOn: boolean; chosen: boolean; setBy: string | null; setAt: string | null };
type Monitor = {
  at: string;
  autoSync: AutoSync;
  workers: Worker[];
  counts: Record<SyncModuleState, number>;
  modules: Module[];
  jobs: { open: Job[]; oldestQueuedMs: number | null; last24h: { type: string; done: number; failed: number; other: number; avgMs: number | null }[]; recentFailures: Job[] };
  watermarks: { resource: string; updateDateWib: string }[];
};

const when = (s: string | null | undefined) => (s ? formatWibLong(new Date(s)) : "—");

/** "12 s", "4 min 10 s", "2 h 5 min". */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min${s % 60 ? ` ${s % 60} s` : ""}`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h${m % 60 ? ` ${m % 60} min` : ""}`;
  return `${Math.floor(h / 24)} d`;
}

/** "3 min ago", from the monitor's own clock. */
const ago = (s: string | null | undefined, now: string) => (s ? `${formatDuration(new Date(now).getTime() - new Date(s).getTime())} ago` : "never");

const STATE: Record<SyncModuleState, { label: string; color: string }> = {
  ok: { label: "OK", color: "var(--stage-received)" },
  running: { label: "Running", color: "var(--stage-ordered)" },
  failing: { label: "Failing", color: "var(--danger)" },
  overdue: { label: "Overdue", color: "var(--budget-warn)" },
  never: { label: "Never run", color: "var(--muted)" },
  paused: { label: "Paused", color: "var(--muted)" },
};

function StateBadge({ state }: { state: SyncModuleState }) {
  const s = STATE[state];
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold" style={{ color: s.color }}>
      <span aria-hidden className={`inline-block w-2 h-2 rounded-full ${state === "running" ? "animate-pulse" : ""}`} style={{ background: s.color }} />
      {s.label}
    </span>
  );
}

const outcomeColor = (o: string) => (o === "success" ? "var(--stage-received)" : o === "failed" ? "var(--stage-rejected)" : "var(--muted)");

function MiniStrip({ runs, label }: { runs: Module["last24h"]["strip"]; label: string }) {
  // Busy modules run hundreds of times a day: the last 60 runs keep the strip one line.
  const shown = runs.slice(-60);
  return (
    <div className="flex gap-[1px] items-center" aria-label={label}>
      {shown.map((r, i) => (
        <span key={i} title={`${when(r.at)}: ${r.outcome}`} className="inline-block w-[3px] h-4 rounded-[1px]" style={{ background: outcomeColor(r.outcome) }} />
      ))}
      {!runs.length && <span className="text-muted">—</span>}
    </div>
  );
}

const PART_COLOR: Record<RunPart["outcome"], string> = { success: "var(--stage-received)", failed: "var(--danger)", skipped: "var(--budget-warn)" };

/** A run's parts (each SEA/PEA database, the nightly clean-up's steps): outcome, records, time, error. */
export function Parts({ parts }: { parts: RunPart[] }) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px]">
      {parts.map((p) => (
        <li key={p.name} title={p.error ?? ""} className="whitespace-nowrap">
          <span aria-hidden style={{ color: PART_COLOR[p.outcome] }}>
            {p.outcome === "success" ? "✓" : p.outcome === "failed" ? "✗" : "–"}
          </span>{" "}
          <span className="font-semibold">{p.name}</span>{" "}
          <span className="text-muted">
            {p.outcome === "failed" ? "failed" : p.outcome === "skipped" ? "skipped" : `${p.records.toLocaleString()} rows`} · {formatDuration(p.ms)}
          </span>
          {p.error && p.outcome !== "success" && <span style={{ color: PART_COLOR[p.outcome] }}> — {p.error.length > 80 ? `${p.error.slice(0, 80)}…` : p.error}</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Stop / Start auto sync: whether the worker starts runs on its own timers. Stopping also ends the
 * backfill, delta or item read it started at their next step; the Sync buttons keep working.
 */
function AutoSyncControl({ a, workerUp }: { a: AutoSync; workerUp: boolean }) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (on: boolean) => api<AutoSync>("/admin/auto-sync", { method: "PUT", body: JSON.stringify({ on }) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-sync-monitor"] }),
  });
  const on = a.on;
  const toggle = () => {
    if (on && !window.confirm("Stop auto sync? The worker starts no runs on its own until you start it again; a backfill, delta or item read in progress stops at its next step. The Sync buttons keep working.")) return;
    set.mutate(!on);
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-input border border-line px-3 py-2 text-[13px]">
      <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: on ? "var(--stage-received)" : "var(--muted)" }}>
        <span aria-hidden className="inline-block w-2 h-2 rounded-full" style={{ background: on ? "var(--stage-received)" : "var(--muted)" }} />
        Auto sync {on ? "on" : "off"}
      </span>
      <span className="text-muted text-[12px]">
        {a.chosen ? `${on ? "started" : "stopped"} by ${a.setBy ?? "—"} ${when(a.setAt)}` : `from SCHEDULED_SYNC; SEA/PEA and KMK ${a.otherOn ? "run" : "do not run"}`}
        {!on && " · data updates only when someone presses Sync"}
        {!workerUp && " · the sync service is not running, so nothing runs either way"}
      </span>
      <button
        className={`ml-auto rounded-input font-semibold px-3 py-1 text-[12.5px] disabled:opacity-50 ${on ? "border border-[var(--danger)] text-[var(--danger)]" : "bg-brand-2 text-white"}`}
        onClick={toggle}
        disabled={set.isPending}
      >
        {set.isPending ? "Saving…" : on ? "Stop auto sync" : "Start auto sync"}
      </button>
      {set.isError && <span className="text-[var(--danger)] text-[12px] w-full">Not saved: {set.error instanceof Error ? set.error.message : "error"}</span>}
    </div>
  );
}

/** The worker processes' liveness: alive (beat within 90 s), uptime, what runs now; or how long silent. */
function Workers({ workers, now }: { workers: Worker[]; now: string }) {
  if (!workers.length)
    return (
      <div className="rounded-input border border-line px-3 py-2 text-[13px]">
        <span className="font-semibold" style={{ color: "var(--danger)" }}>
          Sync service: no heartbeat recorded
        </span>
        <span className="text-muted"> — the worker has not started since this check was added, or cannot write to the store.</span>
      </div>
    );
  return (
    <div className="space-y-1">
      {workers.map((w) => (
        <div key={w.name} className="rounded-input border px-3 py-2 text-[13px]" style={{ borderColor: w.alive ? "var(--line)" : "var(--danger)" }}>
          <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: w.alive ? "var(--stage-received)" : "var(--danger)" }}>
            <span aria-hidden className="inline-block w-2 h-2 rounded-full" style={{ background: w.alive ? "var(--stage-received)" : "var(--danger)" }} />
            Sync service {w.alive ? "up" : `silent for ${formatDuration(w.silentMs)}`}
          </span>
          <span className="text-muted">
            {" "}
            · {w.alive ? `up ${formatDuration(new Date(now).getTime() - new Date(w.startedAt).getTime())}` : `last beat ${when(w.beatAt)}`} · {w.host} pid {w.pid}
            {!w.scheduled && " · auto sync off (jobs only)"}
          </span>
          {w.alive && (
            <div className="text-[12px] text-muted">
              Running now: {w.busy.length ? w.busy.join(", ") : "nothing"} · jobs {Object.entries(w.jobsRunning).map(([lane, n]) => `${lane} ${n}`).join(", ")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** One module's last runs, newest first, with their errors. */
function RunHistory({ kind }: { kind: string }) {
  const q = useQuery({ queryKey: ["admin-sync-runs", kind], queryFn: () => api<Run[]>(`/admin/sync/runs/${encodeURIComponent(kind)}?limit=30`) });
  if (q.isError) return <p className="text-[var(--danger)]">Could not load the runs.</p>;
  if (!q.data) return <p className="text-muted">Loading…</p>;
  if (!q.data.length) return <p className="text-muted">No runs recorded.</p>;
  return (
    <table className="w-full text-[12px]">
      <thead>
        <tr className="text-muted text-left">
          <th className="py-0.5 pr-2">Started</th>
          <th className="pr-2">Duration</th>
          <th className="pr-2">Outcome</th>
          <th className="pr-2 text-right">Changed</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
        {q.data.map((r) => (
          <tr key={r.id} className="border-t border-line align-top">
            <td className="py-0.5 pr-2 whitespace-nowrap">
              {when(r.startedAt)}
              {r.full && <span className="text-muted"> · full</span>}
            </td>
            <td className="pr-2 whitespace-nowrap tnum">{r.finishedAt ? formatDuration(r.durationMs) : "running"}</td>
            <td className="pr-2 font-semibold" style={{ color: outcomeColor(r.outcome) }}>
              {r.outcome}
            </td>
            <td className="pr-2 text-right tnum">{r.recordsChanged.toLocaleString()}</td>
            <td className="break-words">
              {r.errorType ? `${r.errorType}: ${r.errorMessage ?? ""}` : ""}
              {r.parts && <Parts parts={r.parts} />}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ModuleRow({ m, now, open, onToggle }: { m: Module; now: string; open: boolean; onToggle: () => void }) {
  const running = m.latest?.outcome === "running";
  return (
    <>
      <tr className="border-t border-line align-top cursor-pointer hover:bg-[var(--hover)]" onClick={onToggle} aria-expanded={open}>
        <td className="py-1.5 pr-2">
          <div className="font-semibold">
            <span aria-hidden className="text-muted mr-1">
              {open ? "▾" : "▸"}
            </span>
            {m.label}
          </div>
          <div className="text-muted text-[11.5px] pl-4">
            {m.source} · {m.schedule}
          </div>
        </td>
        <td className="pr-2">
          <StateBadge state={m.state} />
          {m.state === "failing" && (
            <div className="text-[11.5px] text-[var(--danger)]">
              {m.failuresInARow} in a row, since {ago(m.failingSince, now)}
            </div>
          )}
          {m.state === "overdue" && <div className="text-[11.5px] text-muted">expected within {formatDuration((m.overdueAfterMinutes ?? 0) * 60_000)}</div>}
          {running && m.state !== "running" && <div className="text-[11.5px] text-muted">running now</div>}
        </td>
        <td className="pr-2 whitespace-nowrap" title={when(m.latest?.startedAt)}>
          {ago(m.latest?.startedAt, now)}
          <div className="text-muted text-[11.5px]">{running ? `for ${formatDuration(new Date(now).getTime() - new Date(m.latest!.startedAt).getTime())}` : formatDuration(m.latest?.durationMs)}</div>
        </td>
        <td className="pr-2 whitespace-nowrap" title={when(m.lastSuccess?.startedAt)}>
          {ago(m.lastSuccess?.startedAt, now)}
        </td>
        <td className="pr-2 whitespace-nowrap tnum">
          {m.last24h.runs ? (
            <>
              {m.last24h.runs} runs
              {m.last24h.failed > 0 && <span className="text-[var(--danger)]"> · {m.last24h.failed} failed</span>}
              <div className="text-muted text-[11.5px]">
                avg {formatDuration(m.last24h.avgDurationMs)} · {m.last24h.recordsChanged.toLocaleString()} changed
              </div>
            </>
          ) : (
            <span className="text-muted">none</span>
          )}
        </td>
        <td className="pt-2">
          <MiniStrip runs={m.last24h.strip} label={`${m.label}: runs in the last 24 hours`} />
        </td>
      </tr>
      {m.lastFailure && m.state === "failing" && (
        <tr>
          <td colSpan={6} className="pb-1.5 pl-6 text-[12px] text-[var(--danger)] break-words">
            {m.lastFailure.errorType}: {m.lastFailure.errorMessage}
          </td>
        </tr>
      )}
      {m.latest?.parts && m.kind !== "nightly" && (
        <tr>
          <td colSpan={6} className="pb-1.5 pl-6">
            <Parts parts={m.latest.parts} />
          </td>
        </tr>
      )}
      {open && (
        <tr>
          <td colSpan={6} className="pb-3 pl-6 pr-2">
            <RunHistory kind={m.kind} />
          </td>
        </tr>
      )}
    </>
  );
}

function Jobs({ j, now }: { j: Monitor["jobs"]; now: string }) {
  return (
    <div className="space-y-3">
      <div className="text-[13px]">
        <span className="font-semibold">Open jobs:</span> {j.open.length}
        {j.oldestQueuedMs != null && (
          <span className={j.oldestQueuedMs > 60_000 ? "text-[var(--danger)]" : "text-muted"}> · oldest queued for {formatDuration(j.oldestQueuedMs)}</span>
        )}
      </div>
      {j.open.length > 0 && (
        <ul className="text-[12.5px] space-y-0.5">
          {j.open.map((x) => (
            <li key={x.id}>
              <span className="font-semibold">{x.type}</span> {x.no} · {x.state}
              {x.state === "running" ? ` for ${ago(x.claimedAt, now).replace(" ago", "")}` : ` since ${ago(x.createdAt, now)}`}
              {x.progress && <span className="text-muted"> · {x.progress.step ?? ""} ({x.progress.done}/{x.progress.total})</span>}
            </li>
          ))}
        </ul>
      )}
      <div>
        <div className="label mb-1">Last 24 h by type</div>
        {j.last24h.length ? (
          <table className="text-[12.5px]">
            <thead>
              <tr className="text-muted text-left">
                <th className="pr-4">Type</th>
                <th className="pr-4 text-right">Done</th>
                <th className="pr-4 text-right">Failed</th>
                <th className="text-right">Avg time</th>
              </tr>
            </thead>
            <tbody>
              {j.last24h.map((t) => (
                <tr key={t.type} className="border-t border-line">
                  <td className="pr-4 py-0.5">{t.type}</td>
                  <td className="pr-4 text-right tnum">{t.done}</td>
                  <td className={`pr-4 text-right tnum ${t.failed ? "text-[var(--danger)] font-semibold" : ""}`}>{t.failed}</td>
                  <td className="text-right tnum">{formatDuration(t.avgMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-muted text-[12.5px]">No jobs</p>
        )}
      </div>
      {j.recentFailures.length > 0 && (
        <div>
          <div className="label mb-1">Recent failed jobs ({j.recentFailures.length})</div>
          <ul className="text-[12.5px] space-y-0.5">
            {j.recentFailures.map((x) => (
              <li key={x.id} className="break-words">
                {when(x.createdAt)} · <span className="font-semibold">{x.type}</span> {x.no} · {x.state}
                {x.error && <span className="text-[var(--danger)]"> — {x.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Every sync module at a glance, refreshed every 30 s. */
export function SyncMonitor() {
  const q = useQuery({ queryKey: ["admin-sync-monitor"], queryFn: () => api<Monitor>("/admin/sync/monitor"), refetchInterval: 30_000 });
  const [openKind, setOpenKind] = useState<string | null>(null);
  const [showWatermarks, setShowWatermarks] = useState(false);
  if (q.isError) return <p className="text-[12.5px] text-[var(--danger)]">Could not load the sync monitor.</p>;
  const d = q.data;
  if (!d) return <p className="text-[12.5px] text-muted">Loading…</p>;
  const order: SyncModuleState[] = ["failing", "overdue", "running", "ok", "paused", "never"];
  return (
    <div className="space-y-4">
      <AutoSyncControl a={d.autoSync} workerUp={d.workers.some((w) => w.alive)} />
      <Workers workers={d.workers} now={d.at} />
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]" role="status">
        {order
          .filter((s) => d.counts[s])
          .map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5">
              <StateBadge state={s} /> <span className="tnum">{d.counts[s]}</span>
            </span>
          ))}
        <span className="text-muted ml-auto text-[12px]">as of {when(d.at)} · click a module for its runs</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px] min-w-[760px]">
          <thead>
            <tr className="text-muted text-left">
              <th className="pr-2 font-normal">Module</th>
              <th className="pr-2 font-normal">Status</th>
              <th className="pr-2 font-normal">Last run</th>
              <th className="pr-2 font-normal">Last success</th>
              <th className="pr-2 font-normal">Last 24 h</th>
              <th className="font-normal">Runs (latest 60)</th>
            </tr>
          </thead>
          <tbody>
            {d.modules.map((m) => (
              <ModuleRow key={m.kind} m={m} now={d.at} open={openKind === m.kind} onToggle={() => setOpenKind(openKind === m.kind ? null : m.kind)} />
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-[16px] font-bold pt-2">Jobs queue</h3>
      <p className="text-[11.5px] text-muted -mt-2">Drawer refreshes, searches, the Sync buttons and KMK updates. A job queued for more than a minute means the worker is not picking jobs up.</p>
      <Jobs j={d.jobs} now={d.at} />

      <div>
        <button className="text-brand hover:underline text-[13px]" onClick={() => setShowWatermarks(!showWatermarks)} aria-expanded={showWatermarks}>
          {showWatermarks ? "Hide" : "Show"} watermarks ({d.watermarks.length})
        </button>
        {showWatermarks && (
          <table className="text-[12.5px] mt-1">
            <tbody>
              {d.watermarks.map((w) => (
                <tr key={w.resource} className="border-t border-line">
                  <td className="pr-4 py-0.5">{w.resource}</td>
                  <td className="tnum">{w.updateDateWib} WIB</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
